/**
 * Tool Progress Indicator.
 *
 * Displays a live progress card while an MCP tool call is executing.
 * Shows the tool name, animated status phases, and a ticking elapsed timer.
 * Especially useful for long-running tools like the workflow builder.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ToolProgressIndicatorProps {
  /** Display name of the tool (already cleaned up) */
  toolName: string;
  /** Summary of what the tool is doing (e.g., prompt excerpt) */
  detail?: string;
  /** Timestamp (Date.now()) when the tool call started */
  startTime: number;
  /** Additional CSS class */
  className?: string;
}

/**
 * Known tool phase sequences for specific tools.
 * Each entry is [minSeconds, label]. The indicator picks the last phase
 * whose minSeconds has elapsed.
 */
const TOOL_PHASES: Record<string, [number, string][]> = {
  'assemble workflow': [
    [0, 'Validating node specs...'],
    [3, 'Fetching node catalog from backend...'],
    [8, 'Pre-validating nodes and edges...'],
    [12, 'Checking Composio integrations...'],
    [18, 'Provisioning tool servers...'],
    [25, 'Building workflow structure...'],
    [35, 'Configuring agent parameters and connections...'],
    [50, 'Publishing workflow to platform...'],
    [70, 'Generating workflow link...'],
    [90, 'Finalizing...'],
  ],
  'build and publish workflow': [
    [0, 'Submitting build request...'],
    [3, 'Generating workflow structure...'],
    [10, 'Creating nodes and connections...'],
    [20, 'Configuring agent parameters...'],
    [35, 'Validating workflow...'],
    [50, 'Publishing to platform...'],
    [65, 'Finalizing...'],
  ],
  'build workflow with progress': [
    [0, 'Submitting build job...'],
    [3, 'Generating workflow structure...'],
    [10, 'Creating nodes and connections...'],
    [20, 'Configuring agent parameters...'],
    [35, 'Validating workflow...'],
    [50, 'Publishing to platform...'],
    [65, 'Finalizing...'],
  ],
  'submit workflow build': [
    [0, 'Submitting build job...'],
    [2, 'Job queued, awaiting start...'],
  ],
  'publish workflow': [
    [0, 'Publishing workflow...'],
    [5, 'Uploading to platform...'],
    [15, 'Finalizing...'],
  ],
  'lint workflow': [
    [0, 'Validating workflow...'],
    [3, 'Checking node connections...'],
    [6, 'Verifying parameters...'],
  ],
  'list workflow nodes': [
    [0, 'Fetching node catalog...'],
    [5, 'Loading agent, tool, and output nodes...'],
    [10, 'Discovering MCP tool nodes...'],
  ],
  'inspect workflow node': [
    [0, 'Fetching node details...'],
    [3, 'Loading parameters and ports...'],
  ],
  'execute workflow': [
    [0, 'Preparing workflow payload...'],
    [3, 'Triggering execution...'],
    [8, 'Workflow queued, waiting for start...'],
  ],
  'get execution status': [
    [0, 'Fetching run status...'],
    [3, 'Loading node results...'],
  ],
  'diagnose workflow run': [
    [0, 'Fetching execution data...'],
    [5, 'Analyzing node outputs and errors...'],
    [12, 'Generating diagnosis...'],
  ],
};

/** Default phases for any unknown tool. */
const DEFAULT_PHASES: [number, string][] = [
  [0, 'Calling tool...'],
  [5, 'Waiting for response...'],
  [15, 'Processing...'],
  [30, 'Still working — this tool may take a moment...'],
  [60, 'Long-running operation — please wait...'],
  [120, 'Still processing — almost there...'],
];

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

export const ToolProgressIndicator: React.FC<ToolProgressIndicatorProps> = ({
  toolName,
  detail,
  startTime,
  className,
}) => {
  const [elapsed, setElapsed] = useState(0);

  // Tick the elapsed counter every second
  useEffect(() => {
    setElapsed(Date.now() - startTime);
    const interval = setInterval(() => {
      setElapsed(Date.now() - startTime);
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  // Determine current phase label
  const phases = useMemo(() => {
    const key = toolName.toLowerCase();
    return TOOL_PHASES[key] ?? DEFAULT_PHASES;
  }, [toolName]);

  const elapsedSeconds = Math.floor(elapsed / 1000);
  const currentPhase = useMemo(() => {
    let label = phases[0][1];
    for (const [minSec, phaseLabel] of phases) {
      if (elapsedSeconds >= minSec) label = phaseLabel;
    }
    return label;
  }, [phases, elapsedSeconds]);

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2 animate-in fade-in slide-in-from-bottom-2 duration-300',
        className,
      )}
    >
      <Loader2 className='size-4 shrink-0 animate-spin text-muted-foreground' />

      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='text-sm font-medium'>{toolName}</span>
          <span className='text-xs text-muted-foreground tabular-nums'>
            {formatElapsed(elapsed)}
          </span>
        </div>

        <p className='text-xs text-muted-foreground truncate mt-0.5'>
          {detail ? `${detail} — ` : ''}
          {currentPhase}
        </p>
      </div>
    </div>
  );
};

export default ToolProgressIndicator;
